import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function Terms() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-terms';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms of Service – AltaLumé ADHD Testing',
      url: `${siteUrl}/terms`,
      description:
        'AltaLumé Mental Health Terms of Service. Our ADHD testing is for informational purposes only and is not intended to diagnose ADHD or any medical condition.',
      inLanguage: 'en-US',
      isPartOf: {
        '@type': 'WebSite',
        name: 'AltaLumé ADHD Testing',
        url: siteUrl,
      },
      dateModified: '2026-03-11',
    });
    if (!document.getElementById('schema-terms')) {
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById('schema-terms');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-violet-50 text-violet-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-file-text-line"></i>
              Terms of Service
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-stone-600 mb-2">
              AltaLumé ADHD Testing
            </p>
            <p className="text-sm text-stone-500">
              Effective Date: March 11, 2026
            </p>
          </div>

          {/* Important Disclaimer Banner */}
          <div className="bg-violet-600 text-white rounded-2xl p-8 mb-12 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-white/20 rounded-full">
                <i className="ri-alert-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Important Medical Disclaimer</h3>
                <p className="text-violet-50 leading-relaxed">
                  The testing provided by AltaLumé is for <strong>informational purposes only</strong> and is <strong>NOT intended to diagnose ADHD</strong> or any other medical condition. Results should be reviewed by a qualified healthcare provider as part of a comprehensive clinical evaluation. This service does not replace professional medical advice, diagnosis, or treatment.
                </p>
              </div>
            </div>
          </div>

          {/* Terms Content */}
          <div className="prose prose-stone max-w-none">
            {/* Section 1 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">1</span>
                Acceptance of Terms
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p>
                  Welcome to AltaLumé Mental Health ("AltaLumé," "we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of our website and ADHD testing services.
                </p>
                <p>
                  By accessing our website or using our services, you agree to be bound by these Terms. If you do not agree to these Terms, you may not use our services.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">2</span>
                Description of Services
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p>
                  AltaLumé provides computerized attention and response-control testing using the IVA-2 Continuous Performance Test (CPT). Our service generates objective testing data and reports based on your performance during the assessment.
                </p>
                <p>
                  The testing measures attention and response control through a standardized computerized assessment. Results are provided in a detailed report format.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">3</span>
                Informational Purpose &amp; Medical Disclaimer
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p className="font-semibold text-stone-900">
                  IMPORTANT: The testing services provided by AltaLumé are for informational purposes only.
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Our testing does <strong>NOT</strong> constitute a medical diagnosis of ADHD or any other condition</li>
                  <li>Test results do <strong>NOT</strong> replace a comprehensive clinical evaluation by a licensed healthcare provider</li>
                  <li>Our service is <strong>NOT</strong> a substitute for professional medical advice, diagnosis, or treatment</li>
                  <li>Results should be reviewed and interpreted by a qualified clinician (such as your PCP, psychiatrist, or psychologist) as part of a complete assessment</li>
                  <li>Only a licensed healthcare professional can diagnose ADHD or recommend treatment</li>
                </ul>
                <p>
                  You should always consult with a qualified healthcare provider regarding any medical concerns or before making any decisions related to your health or treatment.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">4</span>
                Eligibility
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p>
                  To use our services, you must:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Be at least 18 years of age, or have parental/guardian consent if under 18</li>
                  <li>Have the legal capacity to enter into a binding agreement</li>
                  <li>Provide accurate and complete information when registering or using our services</li>
                  <li>Comply with all applicable laws and regulations</li>
                </ul>
              </div>
            </section>

            {/* Section 5 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">5</span>
                User Responsibilities
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p>
                  When using our services, you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Complete the testing in a quiet, distraction-free environment</li>
                  <li>Follow all testing instructions carefully</li>
                  <li>Not attempt to manipulate or falsify test results</li>
                  <li>Not share your test access credentials with others</li>
                  <li>Use the service only for its intended purpose</li>
                  <li>Not use the service for any unlawful or prohibited purpose</li>
                </ul>
              </div>
            </section>

            {/* Section 6 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">6</span>
                Payment &amp; Refunds
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p>
                  Payment is required before accessing the testing service. All fees are clearly displayed prior to purchase.
                </p>
                <p className="font-semibold text-stone-900">
                  Refund Policy:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Refunds may be available if requested <strong>before</strong> the test is administered</li>
                  <li>Once you have started or completed the test, <strong>no refunds will be provided</strong></li>
                  <li>Technical issues that prevent test completion may be evaluated on a case-by-case basis</li>
                </ul>
                <p>
                  All payments are processed securely through third-party payment processors. We do not store your payment card information.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">7</span>
                Intellectual Property
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p>
                  All content on our website and within our services, including but not limited to text, graphics, logos, software, and testing materials, is the property of AltaLumé or its licensors and is protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p>
                  Your test report is provided to you for personal use only. You may not:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Reproduce, distribute, or publicly display the testing software or materials</li>
                  <li>Modify, reverse engineer, or create derivative works</li>
                  <li>Use our content for commercial purposes without written permission</li>
                </ul>
                <p>
                  You may share your personal test report with healthcare providers as needed for your care.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">8</span>
                Limitation of Liability
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p className="font-semibold text-stone-900">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>AltaLumé provides testing services "as is" without warranties of any kind</li>
                  <li>We are not liable for any decisions you make based on test results</li>
                  <li>We are not responsible for any medical outcomes or consequences</li>
                  <li>We do not guarantee that test results will lead to any particular diagnosis or treatment</li>
                  <li>Our total liability shall not exceed the amount you paid for the testing service</li>
                </ul>
                <p>
                  You acknowledge that you are using this service at your own risk and that test results must be interpreted by a qualified healthcare professional.
                </p>
              </div>
            </section>

            {/* Section 9 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">9</span>
                Indemnification
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p>
                  You agree to indemnify, defend, and hold harmless AltaLumé, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your use of our services</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any rights of another party</li>
                  <li>Any decisions or actions you take based on test results</li>
                </ul>
              </div>
            </section>

            {/* Section 10 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">10</span>
                Governing Law
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the state in which AltaLumé operates, without regard to its conflict of law provisions.
                </p>
                <p>
                  Any disputes arising from these Terms or your use of our services shall be resolved in the courts of competent jurisdiction in that state.
                </p>
              </div>
            </section>

            {/* Section 11 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">11</span>
                Changes to Terms
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p>
                  AltaLumé reserves the right to modify these Terms at any time. We will notify users of material changes by:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Posting the updated Terms on our website</li>
                  <li>Updating the "Effective Date" at the top of this document</li>
                  <li>Sending email notification for significant changes (if applicable)</li>
                </ul>
                <p>
                  Your continued use of our services after changes are posted constitutes acceptance of the modified Terms.
                </p>
              </div>
            </section>

            {/* Section 12 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-violet-100 text-violet-600 rounded-lg text-sm font-bold flex-shrink-0">12</span>
                Contact Information
              </h2>
              <div className="pl-11 text-stone-700 leading-relaxed space-y-3">
                <p>
                  If you have questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-stone-50 rounded-xl p-6 mt-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-violet-100 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-violet-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-900">Email</p>
                      <a href="mailto:admin@altalume.com" className="text-violet-600 hover:text-violet-700 transition-colors">
                        admin@altalume.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-violet-100 rounded-lg flex-shrink-0">
                      <i className="ri-global-line text-violet-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-900">Website</p>
                      <span className="text-stone-600">www.altalume.com</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Footer links */}
          <div className="flex flex-wrap gap-4 pt-6 border-t border-stone-100">
            <Link to="/" className="text-sm text-stone-500 hover:text-violet-600 transition-colors cursor-pointer whitespace-nowrap">← Back to Home</Link>
            <Link to="/privacy" className="text-sm text-stone-500 hover:text-violet-600 transition-colors cursor-pointer whitespace-nowrap">Privacy Policy</Link>
            <Link to="/hipaa-notice" className="text-sm text-stone-500 hover:text-violet-600 transition-colors cursor-pointer whitespace-nowrap">HIPAA Notice</Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}